import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardHeader } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Table } from '../components/ui/Table';
import { StatsCard } from '../components/ui/StatsCard';

const ICON = (
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const fmt = (n: any) => {
  if (n == null || n === undefined) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  return x.toLocaleString();
};

// Smart unit formatter — converts large counts to "K"/"M"/"B" suffixes for
// readability and applies unit-specific conversions (seconds→minutes when
// large, MB→GB when large, etc.).
const fmtWithUnit = (n: any, unit: string | null | undefined) => {
  if (n == null) return '—';
  const x = Number(n);
  if (!Number.isFinite(x)) return '—';
  const u = (unit || '').toLowerCase();

  // Bytes / data units
  if (u === 'megabyte' || u === 'mb') {
    if (x >= 1024) return `${(x / 1024).toFixed(2)} GB`;
    return `${x.toLocaleString()} MB`;
  }
  if (u === 'gb' || u === 'gigabyte' || u === 'gb-month') {
    if (x >= 1024) return `${(x / 1024).toFixed(2)} TB`;
    return `${x.toLocaleString(undefined, { maximumFractionDigits: 4 })} GB`;
  }
  if (u === 'byte') {
    if (x >= 1_073_741_824) return `${(x / 1_073_741_824).toFixed(2)} GB`;
    if (x >= 1_048_576)     return `${(x / 1_048_576).toFixed(2)} MB`;
    if (x >= 1024)          return `${(x / 1024).toFixed(2)} KB`;
    return `${x.toLocaleString()} B`;
  }

  // Time units
  if (u === 'second') {
    if (x >= 3600) return `${(x / 3600).toFixed(2)} hrs`;
    if (x >= 60)   return `${(x / 60).toFixed(1)} min`;
    return `${x.toLocaleString()} sec`;
  }
  if (u === 'minute' || u === 'min') {
    if (x >= 60) return `${(x / 60).toFixed(1)} hrs (${x.toLocaleString()} min)`;
    return `${x.toLocaleString(undefined, { maximumFractionDigits: 2 })} min`;
  }
  if (u === 'participant-minute' || u === 'participant-minutes') {
    return `${x.toLocaleString(undefined, { maximumFractionDigits: 1 })} participant-min`;
  }
  if (u === 'hour')   return `${x.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`;

  // Large-count compaction with the supplied unit
  const compact = (v: number) => {
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)}B`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e4) return `${(v / 1e3).toFixed(1)}K`;
    return v.toLocaleString();
  };

  const unitText = unit ? ` ${unit}` : '';
  // For very small fractional quantities (e.g., 0.0003 GB-seconds) keep precision
  if (x !== 0 && Math.abs(x) < 1) return `${x.toFixed(4)}${unitText}`;
  return `${compact(x)}${unitText}`;
};

const dateOnly = (s: string | null) => s ? new Date(s).toLocaleDateString() : '—';

type EventMeter = {
  event_type: string;
  provider: string;
  category: string;
  is_passthrough: boolean;
  unit_label: string | null;
  count_all_time: string;
  count_30d: number; count_3mo: number; count_6mo: number; count_12mo: number;
  qty_30d: string; qty_3mo: string; qty_6mo: string; qty_12mo: string; qty_all_time: string;
  first_seen_at: string | null;
  last_seen_at: string | null;
};
type EntityMeter = {
  table: string;
  unit_label: string;
  count_all_time: string;
  count_30d: number | null;
  count_3mo: number | null;
  count_6mo: number | null;
  count_12mo: number | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  error?: string;
};
type CustomMeter = {
  meter: string;
  unit_label: string;
  count_all_time?: string;
  error?: string;
};

type MetricsResponse = {
  event_meters: EventMeter[];
  entity_meters: EntityMeter[];
  custom_meters: CustomMeter[];
  totals: { event_meters: number; entity_meters: number; custom_meters: number; total_event_occurrences: number };
  generated_at: string;
};

const TABS: Array<{ key: 'events' | 'entities' | 'custom'; label: string }> = [
  { key: 'events',   label: 'Event Meters' },
  { key: 'entities', label: 'Entity Meters' },
  { key: 'custom',   label: 'Custom Meters' },
];

export const Metrics: React.FC = () => {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<'events' | 'entities' | 'custom'>('events');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';
    const token = localStorage.getItem('intrlock_token') || '';
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    fetch(`${API}/operations/metrics`, { headers })
      .then(r => r.json())
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const filterFn = (s: string) =>
    !filter || s.toLowerCase().includes(filter.toLowerCase());

  const eventRows = useMemo(() => {
    if (!data) return [];
    return data.event_meters.filter(m => filterFn(m.event_type) || filterFn(m.provider || ''));
  }, [data, filter]);
  const entityRows = useMemo(() => {
    if (!data) return [];
    return data.entity_meters.filter(m => filterFn(m.table));
  }, [data, filter]);
  const customRows = useMemo(() => {
    if (!data) return [];
    return data.custom_meters.filter(m => filterFn(m.meter));
  }, [data, filter]);

  if (loading) return <div className="text-sm text-gray-400 p-8">Loading metrics…</div>;
  if (!data) return <div className="text-sm text-red-500 p-8">Failed to load /operations/metrics</div>;

  const grand = data.totals;

  return (
    <div className="shadow-page-frame legacy-dashboard-migrated space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Metrics</h1>
        <p className="text-gray-500 mt-1">Every internal counter on the platform — event meters, entity row counts, and derived custom meters. Windowed counts come straight from Postgres.</p>
        <p className="text-xs text-gray-400 mt-1">Last refresh: {new Date(data.generated_at).toLocaleString()}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard title="Event Meters"      value={fmt(grand.event_meters)}            icon={ICON} />
        <StatsCard title="Entity Meters"     value={fmt(grand.entity_meters)}           icon={ICON} />
        <StatsCard title="Custom Meters"     value={fmt(grand.custom_meters)}           icon={ICON} />
        <StatsCard title="Event Occurrences" value={fmt(grand.total_event_occurrences)} icon={ICON} />
      </div>

      <div className="flex flex-wrap items-center gap-3 border-b border-gray-200 pb-3">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium ${tab === t.key
              ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {t.label} ({t.key === 'events' ? data.event_meters.length : t.key === 'entities' ? data.entity_meters.length : data.custom_meters.length})
          </button>
        ))}
        <div className="flex-1" />
        <input
          type="text"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          placeholder="Filter…"
          className="border border-gray-300 rounded-md px-3 py-1.5 text-sm w-56"
        />
      </div>

      {tab === 'events' && (
        <Card padding={false}>
          <CardHeader title="Event Meters" subtitle="Every catalog event with occurrence counts. Powered by the internal_meters table + windowed COUNT() on usage_ledger." />
          <Table
            data={eventRows.map((r, i) => ({ ...r, id: r.event_type || i }))}
            columns={[
              { key: 'event_type',  header: 'Event', render: (r: any) => (
                <div>
                  <span className="font-mono text-xs">{r.event_type}</span>
                  {r.is_passthrough && <span className="ml-2 text-[10px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">passthrough</span>}
                  <span className="block text-[10px] text-gray-500">{r.category}{r.unit_label ? ` · billed in ${r.unit_label}` : ''}</span>
                </div>
              )},
              { key: 'provider',    header: 'Provider', render: (r: any) => <span className="text-xs text-gray-700">{r.provider || '—'}</span> },
              { key: 'count_30d',   header: '30d',      render: (r: any) => (
                <div className="font-mono text-xs">
                  <div>{fmt(r.count_30d)} fires</div>
                  <div className="text-[10px] text-emerald-700">{fmtWithUnit(r.qty_30d, r.unit_label)}</div>
                </div>
              )},
              { key: 'count_3mo',   header: '3mo',      render: (r: any) => (
                <div className="font-mono text-xs">
                  <div>{fmt(r.count_3mo)} fires</div>
                  <div className="text-[10px] text-emerald-700">{fmtWithUnit(r.qty_3mo, r.unit_label)}</div>
                </div>
              )},
              { key: 'count_6mo',   header: '6mo',      render: (r: any) => (
                <div className="font-mono text-xs">
                  <div>{fmt(r.count_6mo)} fires</div>
                  <div className="text-[10px] text-emerald-700">{fmtWithUnit(r.qty_6mo, r.unit_label)}</div>
                </div>
              )},
              { key: 'count_12mo',  header: '12mo',     render: (r: any) => (
                <div className="font-mono text-xs">
                  <div>{fmt(r.count_12mo)} fires</div>
                  <div className="text-[10px] text-emerald-700">{fmtWithUnit(r.qty_12mo, r.unit_label)}</div>
                </div>
              )},
              { key: 'count_all',   header: 'All Time', render: (r: any) => (
                <div className="font-mono text-xs font-semibold">
                  <div>{fmt(r.count_all_time)} fires</div>
                  <div className="text-[10px] text-emerald-700 font-medium">{fmtWithUnit(r.qty_all_time, r.unit_label)}</div>
                </div>
              )},
              { key: 'last_seen',   header: 'Last Seen',render: (r: any) => <span className="text-xs text-gray-500">{dateOnly(r.last_seen_at)}</span> },
            ]}
          />
        </Card>
      )}

      {tab === 'entities' && (
        <Card padding={false}>
          <CardHeader title="Entity Meters" subtitle="Row counts for every public Postgres table. Tables with a created_at column also expose windowed counts." />
          <Table
            data={entityRows.map((r, i) => ({ ...r, id: r.table || i }))}
            columns={[
              { key: 'table',       header: 'Table',    render: (r: any) => (
                <div>
                  <span className="font-mono text-xs">{r.table}</span>
                  <span className="block text-[10px] text-gray-500">counts {r.unit_label}</span>
                </div>
              )},
              { key: 'count_30d',   header: '30d',      render: (r: any) => r.error ? <span className="text-xs text-red-500">err</span> : <span className="font-mono text-xs">{r.count_30d == null ? '—' : `${fmt(r.count_30d)} new`}</span> },
              { key: 'count_3mo',   header: '3mo',      render: (r: any) => <span className="font-mono text-xs">{r.count_3mo == null ? '—' : `${fmt(r.count_3mo)} new`}</span> },
              { key: 'count_6mo',   header: '6mo',      render: (r: any) => <span className="font-mono text-xs">{r.count_6mo == null ? '—' : `${fmt(r.count_6mo)} new`}</span> },
              { key: 'count_12mo',  header: '12mo',     render: (r: any) => <span className="font-mono text-xs">{r.count_12mo == null ? '—' : `${fmt(r.count_12mo)} new`}</span> },
              { key: 'count_all',   header: 'All Time', render: (r: any) => <span className="font-mono text-xs font-semibold">{fmt(r.count_all_time)} {r.unit_label}</span> },
              { key: 'first_seen',  header: 'First Seen', render: (r: any) => <span className="text-xs text-gray-500">{dateOnly(r.first_seen_at)}</span> },
              { key: 'last_seen',   header: 'Last Seen',  render: (r: any) => <span className="text-xs text-gray-500">{dateOnly(r.last_seen_at)}</span> },
            ]}
          />
        </Card>
      )}

      {tab === 'custom' && (
        <Card padding={false}>
          <CardHeader title="Custom Meters" subtitle="Hand-tuned counters that aren't a single table row count — phone numbers, VoIP push tokens, holder counts per provider, etc." />
          <Table
            data={customRows.map((r, i) => ({ ...r, id: r.meter || i }))}
            columns={[
              { key: 'meter', header: 'Meter', render: (r: any) => (
                <div>
                  <span className="font-mono text-xs">{r.meter}</span>
                  <span className="block text-[10px] text-gray-500">unit: {r.unit_label}</span>
                </div>
              )},
              { key: 'count', header: 'Count', render: (r: any) => r.error
                ? <span className="text-xs text-red-500 italic">{r.error}</span>
                : <span className="font-mono text-sm font-semibold">{fmtWithUnit(r.count_all_time, r.unit_label)}</span> },
            ]}
          />
        </Card>
      )}
    </div>
  );
};
